import { Assets, type Texture } from 'pixi.js';
import pLimit from 'p-limit';

/** Pixi Assets 的可注入子集，便于单测替换真实加载/卸载。 */
interface TextureAssets {
  cache: {
    has(key: string): boolean;
  };
  load(input: unknown): Promise<Texture>;
  unload(key: string): Promise<void>;
}

/** 依赖注入：时钟可替换，方便预算相关单测。 */
interface TextureManagerDependencies {
  assets?: TextureAssets;
  now?: () => number;
}

/**
 * 阅读端会话参数：
 * - budgetBytes：暖缓存字节上限，超限按 LRU 淘汰 refs===0 的纹理；
 * - maxConcurrent：全局加载并发上限。
 */
interface ReaderTextureSessionOptions {
  budgetBytes: number;
  maxConcurrent: number;
}

interface TextureLoadOptions {
  parser?: string;
  signal?: AbortSignal;
  timeoutMs?: number;
}

/**
 * 单条纹理跟踪状态。
 * refs：持有次数；bytes：估算占用；lastUsed：LRU 时间戳；
 * evicting：正在卸载，避免重复计入驻留字节。
 */
interface TextureRecord {
  refs: number;
  bytes: number;
  lastUsed: number;
  evicting: boolean;
}

/** mipmap 链约增加 1/3 显存，估算时乘以该系数。 */
const MIPMAP_MEMORY_FACTOR = 4 / 3;

const defaultTextureAssets: TextureAssets = {
  cache: Assets.cache,
  load: (input) => Assets.load(input as never) as Promise<Texture>,
  unload: (key) => Assets.unload(key)
};

/** 与 AbortSignal 语义一致的取消错误，便于上层识别 aborted。 */
const createAbortError = () => new DOMException('Texture load was cancelled', 'AbortError');
const createTimeoutError = () => new DOMException('Texture load timed out', 'TimeoutError');

function waitForTextureLoad(
  task: Promise<Texture>,
  options: Pick<TextureLoadOptions, 'signal' | 'timeoutMs'>
): Promise<Texture> {
  if (options.signal?.aborted) return Promise.reject(createAbortError());
  if (!options.signal && !options.timeoutMs) return task;

  return new Promise<Texture>((resolve, reject) => {
    let settled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const cleanup = () => {
      if (timeoutId !== undefined) clearTimeout(timeoutId);
      options.signal?.removeEventListener('abort', handleAbort);
    };
    const finish = (callback: () => void) => {
      if (settled) return;
      settled = true;
      cleanup();
      callback();
    };
    const handleAbort = () => finish(() => reject(createAbortError()));

    options.signal?.addEventListener('abort', handleAbort, { once: true });
    if (options.timeoutMs && options.timeoutMs > 0) {
      timeoutId = setTimeout(() => finish(() => reject(createTimeoutError())), options.timeoutMs);
    }

    task.then(
      (texture) => finish(() => resolve(texture)),
      (error) => finish(() => reject(error))
    );
  });
}

/**
 * 纹理资源管理器：
 * - 编辑器：未配置阅读会话时，引用归零立即卸载；
 * - 阅读端：启用字节预算 LRU 暖缓存 + 全局加载并发队列。
 */
export class TextureManager {
  private readonly assets: TextureAssets;
  private readonly now: () => number;

  /** url → 引用/字节/LRU 等跟踪记录 */
  private readonly records = new Map<string, TextureRecord>();
  /** 进行中的卸载 Promise，防止同 url 并发 unload，并让新 load 等待旧 unload 完成 */
  private readonly pendingUnloads = new Map<string, Promise<void>>();
  /** 阅读端全局加载并发闸门（默认 6，configureReaderSession 可改） */
  private readonly readerLoadLimit = pLimit(6);
  /** 非 null 表示处于阅读会话，启用暖缓存 / 预算 / 并发限制 */
  private readerSession: ReaderTextureSessionOptions | null = null;

  constructor(dependencies: TextureManagerDependencies = {}) {
    this.assets = dependencies.assets ?? defaultTextureAssets;
    this.now = dependencies.now ?? Date.now;
  }

  /**
   * 开启阅读会话：写入预算/并发，并立刻按新预算淘汰超额暖缓存。
   */
  public configureReaderSession(options: ReaderTextureSessionOptions): void {
    this.readerSession = {
      budgetBytes: Math.max(0, options.budgetBytes),
      maxConcurrent: Math.max(1, options.maxConcurrent)
    };
    this.readerLoadLimit.concurrency = this.readerSession.maxConcurrent;
    this.enforceBudget();
  }

  /**
   * 结束阅读会话：关闭暖缓存策略，引用已归零的纹理立即卸载，并等待卸载完成。
   */
  public async endReaderSession(): Promise<void> {
    this.readerSession = null;

    for (const [url, record] of this.records) {
      if (record.refs === 0) void this.evict(url);
    }

    await this.flushPendingUnloads();
  }

  /**
   * 加载并取得一次纹理引用。
   * 步骤：
   * 1. 若已 aborted，直接抛 AbortError；
   * 2. 若同 url 正在卸载，先 await 完成，避免 load/unload 竞态；
   * 3. acquire 预增 refs（失败时 release 回滚）；
   * 4. Assets.load，成功后按尺寸刷新字节估算并 enforceBudget；
   * 5. 阅读会话内走 readerLoadLimit 排队，编辑器直跑。
   */
  public async loadTexture(url: string, options: TextureLoadOptions = {}): Promise<Texture> {
    const run = async () => {
      if (options.signal?.aborted) throw createAbortError();

      // 步骤2：等同 url 的卸载结束，防止刚 unload 又 load 打到半卸载状态
      const pendingUnload = this.pendingUnloads.get(url);
      if (pendingUnload) await pendingUnload;
      if (options.signal?.aborted) throw createAbortError();

      // 步骤3：先占引用，保证加载期间不被预算淘汰
      this.acquire(url);
      let assetsLoad: Promise<Texture> | null = null;
      try {
        const input = options.parser ? { src: url, parser: options.parser } : url;
        assetsLoad = this.assets.load(input);
        const texture = await waitForTextureLoad(assetsLoad, options);
        this.updateTextureBytes(url, texture);
        if (options.signal?.aborted) throw createAbortError();
        return texture;
      } catch (error) {
        // 加载失败或取消：回滚本次 acquire，避免 refs 泄漏
        this.release(url);
        if (
          assetsLoad &&
          error instanceof DOMException &&
          (error.name === 'AbortError' || error.name === 'TimeoutError')
        ) {
          // Assets.load 本身不可取消；若底层稍后成功，只在没有新持有者时清掉迟到缓存。
          void assetsLoad.then(
            () => {
              const record = this.records.get(url);
              if (record?.refs === 0) void this.evict(url);
            },
            () => undefined
          );
        }
        throw error;
      }
    };

    return this.readerSession ? this.readerLoadLimit(run) : run();
  }

  /**
   * 增加一次引用：刷新 lastUsed，并尝试按预算淘汰其他暖缓存。
   */
  private acquire(url: string): void {
    const record = this.records.get(url) ?? {
      refs: 0,
      bytes: 0,
      lastUsed: this.now(),
      evicting: false
    };

    record.refs += 1;
    record.lastUsed = this.now();
    this.records.set(url, record);
    this.enforceBudget();
  }

  /**
   * 释放一次使用。
   * - refs 仍 >0：仅递减；
   * - refs 归零且无阅读会话：立即 evict（编辑器路径）；
   * - refs 归零且有阅读会话：进入暖缓存，仅按预算 LRU 淘汰。
   */
  public release(url: string): void {
    const record = this.records.get(url);
    if (!record) return;

    if (record.refs > 1) {
      record.refs -= 1;
      record.lastUsed = this.now();
      return;
    }

    record.refs = 0;
    record.lastUsed = this.now();

    if (!this.readerSession) {
      void this.evict(url);
      return;
    }

    this.enforceBudget();
  }

  /** 等待所有进行中的 unload 结束（循环直到 pendingUnloads 清空）。 */
  public async flushPendingUnloads(): Promise<void> {
    while (this.pendingUnloads.size > 0) {
      await Promise.all(Array.from(this.pendingUnloads.values()));
    }
  }

  public get trackedCount(): number {
    return this.records.size;
  }

  /** 当前估算驻留字节（排除正在 evicting 的条目，避免预算计算重复扣减）。 */
  public get estimatedResidentBytes(): number {
    let total = 0;
    this.records.forEach((record) => {
      if (!record.evicting) total += record.bytes;
    });
    return total;
  }

  /**
   * 阅读端字节预算：超限时只淘汰暖缓存（refs===0 且未在卸载），按 lastUsed 升序（LRU）。
   * 正在使用的纹理（refs>0）不会被预算强制卸掉。
   */
  private enforceBudget(): void {
    const budgetBytes = this.readerSession?.budgetBytes;
    if (budgetBytes === undefined || this.estimatedResidentBytes <= budgetBytes) return;

    const warmRecords = Array.from(this.records.entries())
      .filter(([, record]) => record.refs === 0 && !record.evicting)
      .sort((left, right) => left[1].lastUsed - right[1].lastUsed);

    let residentBytes = this.estimatedResidentBytes;
    for (const [url, record] of warmRecords) {
      if (residentBytes <= budgetBytes) break;
      residentBytes -= record.bytes;
      void this.evict(url);
    }
  }

  /**
   * 卸载纹理并移除跟踪记录。
   * 步骤：
   * 1. 若已有 pending unload，复用同一 Promise（去重）；
   * 2. refs>0 则拒绝卸载（保护仍在使用的纹理）；
   * 3. 标记 evicting，再 Assets.unload；
   * 4. finally：若期间未被重新 acquire，删除 record；否则只清 evicting 标记。
   */
  private evict(url: string): Promise<void> {
    const existing = this.pendingUnloads.get(url);
    if (existing) return existing;

    const record = this.records.get(url);
    if (!record || record.refs > 0) return Promise.resolve();

    record.evicting = true;

    // 延后一拍执行，确保 finally 清理前 pendingUnloads 已登记，避免 cache miss 时留下已完成任务。
    const task = Promise.resolve().then(async () => {
      try {
        if (this.assets.cache.has(url)) await this.assets.unload(url);
      } finally {
        // 卸载期间若又被 load/acquire，refs 会 >0，此时保留 record，仅结束 evicting
        const latest = this.records.get(url);
        if (latest === record && latest.refs === 0) {
          this.records.delete(url);
        } else if (latest) {
          latest.evicting = false;
        }
        this.pendingUnloads.delete(url);
      }
    });

    this.pendingUnloads.set(url, task);
    return task;
  }

  /** 按宽高 × RGBA(4) × mipmap 系数估算显存占用。 */
  private estimateTextureBytes(texture: Texture): number {
    const width = Math.max(0, texture.source.width ?? 0);
    const height = Math.max(0, texture.source.height ?? 0);
    return Math.ceil(width * height * 4 * MIPMAP_MEMORY_FACTOR);
  }

  /**
   * 加载成功后写入字节估算；取 max 避免同 url 重复加载时估算回退变小，
   * 并触发预算检查。
   */
  private updateTextureBytes(url: string, texture: Texture): void {
    const record = this.records.get(url);
    if (!record) return;
    record.bytes = Math.max(record.bytes, this.estimateTextureBytes(texture));
    this.enforceBudget();
  }
}

export const textureManager = new TextureManager();
